// src/components/SensorGrid.js
import React, { useMemo, useState, useEffect, useRef } from 'react';
import DataGrid from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import './SensorGrid.css';

const SensorGrid = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [viewMode, setViewMode] = useState('pagination'); // 'pagination' or 'scroll'
  const containerRef = useRef(null);

  // 데이터 변경 시 페이지 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // 숫자 포맷팅 템플릿 함수
const formatNumber = (value) =>
  typeof value === "number" ? value.toLocaleString() : (value ?? 'N/A');

  // 데이터 계산
  const totalPages = Math.ceil(data.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentData = viewMode === 'pagination' 
    ? data.slice(startIndex, startIndex + pageSize)
    : data;

  // 컬럼 정의
  const columns = useMemo(() => [
    { key: 'id', name: 'ID', width: '10%', frozen: true },
    { 
      key: 'created_at', 
      name: '생성시간', 
      width: '20%',
      renderCell: ({ row }) => row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A'
    },
    { key: 'topic', name: '설비명', width: '12%' },
    { key: 'machine', name: '센서명', width: '18%' },
    { 
      key: 'temp', 
      name: '온도(°C)', 
      width: '10%',
      renderCell: ({ row }) => formatNumber(row.temp)    
    },
    { 
      key: 'min', 
      name: '최소값', 
      width: '10%',
      renderCell: ({ row }) => formatNumber(row.min)
    },
    { key: 'max', 
      name: '최대값', 
      width: '10%',
      renderCell: ({ row }) => formatNumber(row.max)
    },
    { 
      key: 'ispass', 
      name: '상태', 
      width: '10%',
      renderCell: ({ row }) => (
        <span className={row.ispass ? 'status-pass' : 'status-fail'}>
          {row.ispass ? 'PASS' : 'FAIL'}
        </span>
      )
    }
  ], []);

  const rows = useMemo(() => 
    currentData.map((item, index) => ({ ...item, id: startIndex + index + 1 })), 
    [currentData, startIndex, data]
  );

  // 빈 데이터 처리
  if (data.length === 0) {
    return (
      <div className="no-result-container">
        <div className="no-result-content">
          <div className="no-result-icon">📊</div>
          <div>No Result</div>
          <div className="no-result-subtitle">검색 조건에 맞는 데이터가 없습니다.</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`sensor-grid-container ${viewMode}`}>
      <DataGrid
        columns={columns}
        rows={rows}
        defaultColumnOptions={{ sortable: true, resizable: true }}
        className="rdg-light sensor-grid"
        rowHeight={33}
        headerRowHeight={40}
      />
      
      {/* 컨트롤 바 */}
      <div className="pagination-container">
        <div className="pagination-left">
          <span>표시 모드:</span>
          <select 
            value={viewMode} 
            onChange={(e) => { setViewMode(e.target.value); setCurrentPage(1); }}
            className="pagination-select"
          >
            <option value="pagination">페이지네이션</option>
            <option value="scroll">스크롤</option>
          </select>
          
          {viewMode === 'pagination' && (
            <>
              <span style={{ marginLeft: '15px' }}>페이지 크기:</span>
              <select 
                value={pageSize} 
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                className="pagination-select"
              >
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </>
          )}
          
          <span className="pagination-info">
            {viewMode === 'pagination' 
              ? `총 ${data.length}건 중 ${startIndex + 1}-${Math.min(startIndex + pageSize, data.length)}건 표시`
              : `총 ${data.length}건 표시`
            }
          </span>
        </div>
        
        {viewMode === 'pagination' && (
          <div className="pagination-right">
            {['처음', '이전', '다음', '마지막'].map((label, idx) => {
              const isFirst = idx < 2;
              const disabled = isFirst ? currentPage === 1 : currentPage === totalPages;
              const onClick = () => {
                if (idx === 0) setCurrentPage(1);
                else if (idx === 1) setCurrentPage(currentPage - 1);
                else if (idx === 2) setCurrentPage(currentPage + 1);
                else setCurrentPage(totalPages);
              };
              
              return (
                <button 
                  key={label}
                  onClick={onClick}
                  disabled={disabled}
                  className="pagination-button"
                >
                  {label}
                </button>
              );
            })}
            <span className="pagination-current">{currentPage} / {totalPages}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SensorGrid;